// Details-page interactions: ART/INFO view toggle,
// wallet-dependent button visibility, and every trade action
// (buy / bid / accept / cancel / list / delist / auction). Buttons carry
// their data via data-* attributes rendered server-side.
import { currentAccount, ensureConnected } from "./connect.js";
import { api, flows, external } from "./trade.js";
import type { AttributeFilter } from "../client/api.js";
import { confirmWithFee, modal, promptField, toast } from "./ui.js";
import { pendingChanges, recordChange, type PlacedBid } from "./local-changes.js";
import { placementUi, placedBids, pricesToSats } from "./collection-bid.js";
import { priceGrid } from "./price-grid.js";
import { t } from "../client/i18n.js";
import { markStink } from "../client/stink.js";

const root = document.getElementById("detail");
if (root) init(root);

function init(root: HTMLElement) {
  const monkeId = Number(root.dataset.monkeId);
  const inscriptionId = root.dataset.inscription!;
  const owner = root.dataset.owner || null;

  // ---- small local formatters (fmt lives server-side) ----------------------
  const btcShort = (sats: number) => {
    const b = sats / 1e8;
    if (b >= 1000) return `${Math.round(b).toLocaleString("en-US")} BTC`;
    if (b >= 1) return `${b.toFixed(2).replace(/\.?0+$/, "")} BTC`;
    const dp = b >= 0.0001 ? 4 : 8;
    return `${b.toFixed(dp).replace(/0+$/, "").replace(/\.$/, "")} BTC`;
  };
  const shortAddr = (a: string, n = 6) =>
    a.length <= n * 2 + 1 ? a : `${a.slice(0, n)}…${a.slice(-n)}`;
  // BTC amount entered by the user → integer sats.
  const btcToSats = (v: string) => {
    const b = Number(v);
    if (!Number.isFinite(b) || b <= 0) throw new Error(t("client.details.enter_amount"));
    const n = Math.round(b * 1e8);
    if (n <= 0 || Math.abs(b * 1e8 - n) > 0.001)
      throw new Error(t("client.details.max_decimals"));
    return n;
  };

  let busy = false;
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    busy = true;
    try {
      await fn();
    } catch (err) {
      toast(String((err as Error)?.message ?? err), "error");
    } finally {
      busy = false;
    }
  };
  const reload = () => setTimeout(() => location.reload(), 1200);

  // ---- in-place page updates (no reload) -----------------------------------
  // Actions mutate the already-rendered DOM instead of reloading: the server
  // state is already updated when the API call returns, and a reload would
  // only re-render what we can flip locally.
  const setListedState = (listing: {
    kind: "public" | "private";
    id: string;
    lister: string;
    priceSats: number;
  } | null) => {
    const buyBtn = document.getElementById("buy-btn");
    const delistBtn = document.getElementById("delist-btn");
    const listBtn = document.getElementById("list-btn");
    if (listing) {
      if (buyBtn) {
        buyBtn.dataset.listingKind = listing.kind;
        buyBtn.dataset.listingId = listing.id;
        buyBtn.dataset.price = btcShort(listing.priceSats);
      }
      if (delistBtn) {
        delistBtn.dataset.listingKind = listing.kind;
        delistBtn.dataset.listingId = listing.id;
        delistBtn.dataset.lister = listing.lister;
        delistBtn.hidden = false;
      }
      if (listBtn) listBtn.hidden = true;
    } else {
      if (buyBtn) {
        delete buyBtn.dataset.listingId;
        buyBtn.hidden = true;
      }
      if (delistBtn) {
        delete delistBtn.dataset.listingId;
        delistBtn.hidden = true;
      }
      if (listBtn) listBtn.hidden = false;
    }
    refresh();
  };
  const setPendingSale = () => {
    for (const id of ["buy-btn", "bid-btn", "owner-actions"]) {
      const el = document.getElementById(id);
      if (el) el.hidden = true;
    }
    const actions = document.getElementById("actions");
    if (actions && !document.getElementById("pending-sale-note")) {
      const note = document.createElement("p");
      note.id = "pending-sale-note";
      note.className = "dim";
      note.textContent = t("client.details.sale_pending");
      actions.parentElement?.insertBefore(note, actions);
    }
  };
  // A monkes-scoped bid's rows in the COLLECTION / TRAIT OFFERS table (one
  // per component listing this monke). The section only exists server-side
  // when it has rows — absent, the journal (or the next render) shows it
  // instead.
  const insertScopedRows = (bid: PlacedBid) => {
    const anyRow = document.querySelector("[data-scoped-row]");
    const rows = anyRow?.closest("tbody");
    if (!rows) return;
    for (const c of bid.components) {
      const monkes = (c.attributeFilter as { monkes?: number[] } | null)?.monkes;
      if (!Array.isArray(monkes) || !monkes.includes(monkeId)) continue;
      const key = `${bid.id}:${c.componentIndex}`;
      if (document.querySelector(`[data-scoped-row="${key}"]`)) continue;
      const tr = document.createElement("tr");
      tr.dataset.scopedRow = key;
      tr.dataset.bid = bid.id;
      tr.dataset.component = String(c.componentIndex);
      tr.dataset.offer = c.offerId;
      tr.innerHTML =
        `<td></td>` +
        `<td class="num"><span class="price-btc"></span></td>` +
        `<td><a></a></td><td></td>`;
      tr.children[0]!.textContent = `#${monkeId}${c.left > 1 ? t("client.details.left", { n: c.left }) : ""}`;
      tr.querySelector(".price-btc")!.textContent = btcShort(c.priceSats);
      const a = tr.querySelector("a")!;
      a.href = `/account/${bid.bidder}`;
      a.textContent = shortAddr(bid.bidder);
      markStink(a, bid.bidder);
      rows.prepend(tr);
    }
  };
  const removeBidRow = (selector: string) => {
    const row = document.querySelector(selector);
    const tbody = row?.parentElement;
    row?.remove();
    if (tbody && tbody.children.length === 0) {
      tbody.closest("section")?.setAttribute("hidden", "");
    }
  };
  const mkBtn = (label: string) => {
    const b = document.createElement("button");
    b.className = "btn";
    b.textContent = label;
    b.style.marginTop = "12px";
    return b;
  };
  const fieldStyle =
    "background:var(--bg);border:1px solid var(--border);color:var(--fg);" +
    "font-family:inherit;font-size:18px;padding:4px 8px;width:100%";

  // ---- ← / → arrow keys page through the collection by monke number --------
  // Wraps at the ends (10000 → 1). Suspended while typing in a field or
  // while any modal is open, and never fires with modifier keys held.
  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const t = e.target as HTMLElement | null;
    if (t?.closest("input, textarea, select, [contenteditable]")) return;
    if (document.querySelector(".modal-backdrop")) return;
    const next =
      e.key === "ArrowRight" ? (monkeId % 10_000) + 1 : ((monkeId + 9_998) % 10_000) + 1;
    location.href = `/${next}`;
  });

  // ---- VIEW: ART ⇄ switch (body.art-first reflows the page) ----------------
  // The button shows the CURRENT mode ("VIEW:" prefix marks it as state, the
  // ⇄ marks the button as the action); the tooltip names the destination.
  const viewBtn = document.getElementById("view-toggle");
  const viewMode = document.getElementById("vt-mode");
  const artView = () => localStorage.getItem("mdx-detail-view") !== "info";
  const applyView = () => {
    const art = artView();
    document.body.classList.toggle("art-first", art);
    if (viewMode) viewMode.textContent = art ? t("client.details.view_art") : t("client.details.view_info");
    if (viewBtn) {
      viewBtn.title = art ? t("client.details.switch_to_info") : t("client.details.switch_to_art");
      viewBtn.setAttribute(
        "aria-label",
        art ? t("client.details.view_aria_art") : t("client.details.view_aria_info"),
      );
    }
  };
  applyView();
  viewBtn?.addEventListener("click", () => {
    localStorage.setItem("mdx-detail-view", artView() ? "info" : "art");
    applyView();
  });

  // ---- DOWNLOAD IMAGE: pick a background -----------------------------------
  // The link stays a plain <a> to the default (orange) URL for no-JS; with JS
  // the click opens a chooser and the pick navigates to ?bg=…, which the
  // server serves as an attachment (a download, not a page change).
  const dlLink = document.getElementById("dl-image");
  dlLink?.addEventListener("click", (e) => {
    e.preventDefault();
    const { body, close } = modal(t("client.details.download_title", { id: monkeId }));
    for (const [bg, label] of [
      ["orange", t("client.details.bg_orange")],
      ["tan", t("client.details.bg_tan")],
      ["transparent", t("client.details.bg_transparent")],
    ] as const) {
      const b = mkBtn(label);
      b.style.display = "block";
      b.onclick = () => {
        close();
        location.href = `/${monkeId}/image.png?bg=${bg}`;
      };
      body.appendChild(b);
    }
  });

  // ---- wallet-dependent visibility ------------------------------------------
  const refresh = () => {
    const account = currentAccount();
    const isOwner =
      account != null && owner != null && account.ordinalsAddress === owner;
    const show = (el: HTMLElement | null, on: boolean) => {
      if (el) el.hidden = !on;
    };
    // BUY only exists while something is listed: the server renders
    // data-listing-id when for sale and setListedState() maintains it —
    // mirror the parse-time gate instead of unhiding for every non-owner.
    const bb = document.getElementById("buy-btn");
    show(bb, !isOwner && !!bb?.dataset.listingId);
    show(document.getElementById("bid-btn"), !isOwner);
    show(document.getElementById("owner-actions"), isOwner);
    show(document.getElementById("connect-btn"), account == null);
    for (const b of document.querySelectorAll<HTMLElement>("[data-accept-offer]"))
      b.hidden = !isOwner;
    for (const b of document.querySelectorAll<HTMLElement>("[data-sell-offer]"))
      b.hidden = !isOwner;
    for (const b of document.querySelectorAll<HTMLElement>("[data-cancel-offer]")) {
      b.hidden = !(account != null && account.ordinalsAddress === b.dataset.bidder);
    }
  };
  refresh();

  document.getElementById("connect-btn")?.addEventListener("click", async () => {
    await ensureConnected();
    refresh();
  });

  // ---- BUY (public / private fixed-price) -----------------------------------
  const buyBtn = document.getElementById("buy-btn");
  buyBtn?.addEventListener("click", () =>
    run(async () => {
      const account = await ensureConnected();
      if (!account) return;
      refresh();
      const listingId = buyBtn.dataset.listingId;
      if (!listingId) throw new Error(t("client.details.not_listed"));
      const kind = buyBtn.dataset.listingKind === "public" ? "public" : "private";
      // Hardware guard before the fee picker — the shared BUY button can't be
      // hidden per listing kind, so fail here instead of after fee selection.
      if (kind === "private") flows.requireSoftwareSigner(account, t("client.details.buying_private"));
      const rate = await confirmWithFee(
        t("client.details.buy"),
        [
          [t("client.details.monke"), `#${monkeId}`],
          [t("client.details.price"), buyBtn.dataset.price ?? ""],
        ],
        t("client.details.sign"),
        external.getFeeTiers(),
        { feeFor: flows.quoteBuy(account, listingId, kind), usd: external.getBtcUsd() },
      );
      if (rate == null) return;
      const txs =
        kind === "public"
          ? await flows.buyPublic(account, listingId, rate)
          : await flows.buyPrivate(account, listingId, rate);
      toast(t("client.details.bought", { id: monkeId, txs: txs.map((x) => x.name).join(" + ") }), "ok");
      setListedState(null);
      setPendingSale();
      recordChange({ t: "listing-cancelled", id: listingId, inscriptionId, monkeId });
      recordChange({ t: "sale-pending", inscriptionId, monkeId });
    }),
  );

  // ---- PLACE BID: a monkes-scoped bid on exactly this monke, created as-is
  // from free coins (or after a rebalance / combine prompt when the wallet
  // can't fund it as-is) ----------------------------------------------------
  document.getElementById("bid-btn")?.addEventListener("click", async () => {
    const account = await ensureConnected();
    if (!account) return;
    refresh();
    const filter = { monkes: [monkeId] } as AttributeFilter;
    const { body, close } = modal(t("client.details.place_bid_title", { id: monkeId }));
    const grid = priceGrid({ count: 1, pricing: "same", label: t("client.details.bid_amount") });
    body.appendChild(grid.host);
    const go = mkBtn(t("client.details.place_bid"));
    go.onclick = () =>
      run(async () => {
        const prices = pricesToSats(grid.read().priceText, "", "same", 1);
        go.disabled = true;
        go.textContent = t("client.details.signing");
        let placed: PlacedBid[];
        try {
          const { result } = await flows.createBid(
            account,
            { kind: "list", count: 1, pricing: "same", components: [{ attributeFilter: filter, prices }] },
            placementUi(),
            (label) => {
              go.textContent = `${label.toUpperCase()}…`;
            },
          );
          placed = placedBids(result, account.ordinalsAddress);
        } catch (err) {
          if (err instanceof flows.PlacementCancelled) return;
          throw err;
        } finally {
          go.disabled = false;
          go.textContent = t("client.details.place_bid");
        }
        close();
        toast(t("client.details.bid_placed", { price: btcShort(prices[0]!) }), "ok");
        for (const bid of placed) {
          insertScopedRows(bid);
          recordChange({ t: "bid-placed", bid });
        }
      });
    body.appendChild(go);
    grid.priceInput.focus();
  });

  // ---- owner: ACCEPT a bid / bidder: CANCEL own bid --------------------------
  // Wired per row so rows inserted after placing a bid behave identically.

  // ---- owner: SELL NOW into a bid ---------------------------------------------
  for (const btn of document.querySelectorAll<HTMLElement>("[data-sell-offer]")) {
    btn.addEventListener("click", () =>
      run(async () => {
        const account = currentAccount();
        if (!account) throw new Error(t("client.details.connect_first"));
        // The fill is the bid's highest-priced live slot that accepts this
        // monke — ask the server which offer that is right now.
        const row = btn.closest<HTMLElement>("tr[data-bid]");
        let offerId = btn.dataset.sellOffer!;
        let price = btn.dataset.price!;
        if (row?.dataset.bid) {
          const best = await api.bidsApi.best(row.dataset.bid, Number(row.dataset.component ?? 0), inscriptionId);
          offerId = best.offerId;
          price = btcShort(best.priceSats);
        }
        const rate = await confirmWithFee(
          t("client.details.sell_now"),
          [[t("client.details.sell"), t("client.details.sell_prompt", { id: monkeId, price })]],
          t("client.details.sign"),
          external.getFeeTiers(),
          {
            feeFor: flows.quoteSellCollection(offerId, inscriptionId),
            usd: external.getBtcUsd(),
          },
        );
        if (rate == null) return;
        const txs = await flows.acceptCollectionItem(account, offerId, inscriptionId, rate);
        toast(t("client.details.sold", { txs: txs.map((x) => x.name).join(" + ") }), "ok");
        if (row?.dataset.bid) {
          removeBidRow(`[data-scoped-row="${row.dataset.scopedRow}"]`);
          recordChange({ t: "slot-filled", bidId: row.dataset.bid, offerId });
        } else {
          removeBidRow(`[data-scoped-row="${btn.dataset.sellOffer}"]`);
          recordChange({ t: "coll-cancelled", id: btn.dataset.sellOffer! });
        }
        setPendingSale();
        recordChange({ t: "sale-pending", inscriptionId, monkeId });
      }),
    );
  }

  // ---- owner: DELIST ----------------------------------------------------------
  const delistBtn = document.getElementById("delist-btn");
  delistBtn?.addEventListener("click", () =>
    run(async () => {
      if (!delistBtn.dataset.listingId) throw new Error(t("client.details.not_listed"));
      const kind =
        delistBtn.dataset.listingKind === "public" ? "public listing" : "private listing";
      const listingId = delistBtn.dataset.listingId;
      await flows.cancelOrder(kind, listingId, delistBtn.dataset.lister!);
      toast(t("client.details.delisted"), "ok");
      setListedState(null);
      recordChange({ t: "listing-cancelled", id: listingId, inscriptionId, monkeId });
    }),
  );

  // ---- owner: LIST (fixed price, public or private w/ allowlist) ---------------
  document.getElementById("list-btn")?.addEventListener("click", async () => {
    const account = await ensureConnected();
    if (!account) return;
    refresh();
    const { body, close } = modal(t("client.details.list_title", { id: monkeId }));
    const price = promptField(body, t("client.details.price_btc"));
    const privLabel = document.createElement("label");
    privLabel.style.cssText = "display:block;margin:8px 0";
    const privCheck = document.createElement("input");
    privCheck.type = "checkbox";
    privLabel.append(privCheck, document.createTextNode(t("client.details.private_only")));
    body.appendChild(privLabel);
    const taLabel = document.createElement("label");
    taLabel.style.cssText = "display:none;margin:8px 0";
    taLabel.textContent = t("client.details.allowlist_label");
    const ta = document.createElement("textarea");
    ta.rows = 3;
    ta.style.cssText = fieldStyle;
    taLabel.appendChild(ta);
    body.appendChild(taLabel);
    privCheck.onchange = () => {
      taLabel.style.display = privCheck.checked ? "block" : "none";
    };
    const go = mkBtn(t("client.details.create"));
    go.onclick = () =>
      run(async () => {
        const p = btcToSats(price.value);
        go.disabled = true;
        go.textContent = t("client.details.signing");
        let created: { id: string };
        let kind: "public" | "private";
        try {
          if (privCheck.checked) {
            const participants = ta.value
              .split("\n")
              .map((x) => x.trim())
              .filter(Boolean);
            created = await flows.listPrivate(account, inscriptionId, p, participants);
            kind = "private";
          } else {
            created = await flows.listPublic(account, inscriptionId, p);
            kind = "public";
          }
        } finally {
          go.disabled = false;
          go.textContent = t("client.details.create");
        }
        close();
        toast(t("client.details.listed", { id: monkeId, price: btcShort(p) }), "ok");
        setListedState({
          kind,
          id: created.id,
          lister: account.ordinalsAddress,
          priceSats: p,
        });
        recordChange({
          t: "listing-created",
          listing: {
            id: created.id,
            kind,
            inscriptionId,
            monkeId,
            priceSats: p,
            lister: account.ordinalsAddress,
          },
        });
      });
    body.appendChild(go);
    price.focus();
  });

  // Mirrors the API's DUTCH_MAX_STEPS (bitcoin.ts): a step every 3 blocks,
  // 1,001 steps = 3,000 blocks = 500 hours.
  const DUTCH_MAX_STEPS = 1001;
  const DUTCH_MAX_HOURS = Math.floor(((DUTCH_MAX_STEPS - 1) * 3 * 10) / 60);
  // ---- owner: AUCTION (dutch / english) ----------------------------------------
  // English auctions stay off until their bid-route fixes land: with one kind
  // offered the type select is not rendered and the modal is dutch-only.
  const AUCTION_KINDS: readonly ("dutch" | "english")[] = ["dutch"];
  document.getElementById("auction-btn")?.addEventListener("click", async () => {
    const account = await ensureConnected();
    if (!account) return;
    refresh();
    const { body, close } = modal(t("client.details.auction_title", { id: monkeId }));
    const selLabel = document.createElement("label");
    selLabel.style.cssText = "display:block;margin:8px 0";
    selLabel.textContent = t("client.details.auction_type");
    const sel = document.createElement("select");
    sel.style.cssText = fieldStyle;
    for (const value of AUCTION_KINDS) {
      const opt = document.createElement("option");
      opt.value = value;
      opt.textContent = value === "dutch" ? t("client.details.dutch") : t("client.details.english");
      sel.appendChild(opt);
    }
    selLabel.appendChild(sel);
    if (AUCTION_KINDS.length > 1) body.appendChild(selLabel);
    const start = promptField(body, t("client.details.start_price"));
    const reserve = promptField(body, t("client.details.reserve_price"));
    const maxBid = promptField(body, t("client.details.max_bid"));
    const hours = promptField(body, t("client.details.duration"), "", "24");
    const note = document.createElement("p");
    note.className = "action-note";
    body.appendChild(note);
    const sync = () => {
      const isDutch = sel.value === "dutch";
      start.parentElement!.style.display = isDutch ? "" : "none";
      maxBid.parentElement!.style.display = isDutch ? "none" : "";
      note.textContent = isDutch
        ? t("client.details.dutch_note")
        : t("client.details.english_note");
    };
    sel.onchange = sync;
    sync();
    const go = mkBtn(t("client.details.create"));
    go.onclick = () =>
      run(async () => {
        const h = Number(hours.value);
        if (!(h > 0)) throw new Error(t("client.details.enter_duration"));
        go.disabled = true;
        go.textContent = t("client.details.signing");
        try {
          if (sel.value === "dutch") {
            const startSats = btcToSats(start.value);
            const endSats = btcToSats(reserve.value);
            // A step every 3 blocks; span must be a positive multiple of 3,
            // and the API stores at most DUTCH_MAX_STEPS pre-signed steps —
            // refuse here, before the wallet prompt.
            const span = Math.max(3, Math.ceil((h * 6) / 3) * 3);
            if (span / 3 + 1 > DUTCH_MAX_STEPS) {
              throw new Error(t("client.details.dutch_too_long", { hours: DUTCH_MAX_HOURS }));
            }
            const tip = await external.getTipHeight();
            const steps = await flows.createDutch(account, inscriptionId, {
              startBlock: tip + 1,
              endBlock: tip + 1 + span,
              startPriceSats: startSats,
              endPriceSats: endSats,
              expiryBlocks: null,
            });
            toast(
              t("client.details.dutch_live", { steps, start: btcShort(startSats), end: btcShort(endSats) }),
              "ok",
            );
          } else {
            const r = btcToSats(reserve.value);
            const max = maxBid.value.trim() ? btcToSats(maxBid.value) : undefined;
            const startMs = Date.now();
            const txid = await flows.createEnglish(account, inscriptionId, {
              reservePriceSats: r,
              maxBidSats: max,
              allowUnconfirmedBids: true,
              startAt: new Date(startMs).toISOString(),
              initialEndAt: new Date(startMs + h * 3600_000).toISOString(),
            });
            toast(
              t("client.details.english_created", { txid: shortAddr(txid, 6) }),
              "ok",
            );
          }
        } finally {
          go.disabled = false;
          go.textContent = t("client.details.create");
        }
        close();
        reload();
      });
    body.appendChild(go);
  });

  // Layer recent local changes over this (possibly SWR-cached) render —
  // idempotent, scoped to THIS inscription where the change is item-bound.
  for (const c of pendingChanges()) {
    if (c.t === "bid-placed") {
      insertScopedRows(c.bid);
    } else if (c.t === "bid-cancelled") {
      for (const row of document.querySelectorAll(`[data-scoped-row][data-bid="${c.bidId}"]`)) {
        removeBidRow(`[data-scoped-row="${(row as HTMLElement).dataset.scopedRow}"]`);
      }
    } else if (c.t === "component-cancelled") {
      removeBidRow(`[data-scoped-row="${c.bidId}:${c.componentIndex}"]`);
    } else if (c.t === "slot-filled") {
      const row = document.querySelector<HTMLElement>(`[data-scoped-row][data-offer="${c.offerId}"]`);
      if (row) removeBidRow(`[data-scoped-row="${row.dataset.scopedRow}"]`);
    } else if (c.t === "coll-cancelled") {
      const row = document.querySelector<HTMLElement>(`[data-scoped-row][data-offer="${c.id}"]`);
      if (row) removeBidRow(`[data-scoped-row="${row.dataset.scopedRow}"]`);
    } else if (c.t === "listing-created" && c.listing.inscriptionId === inscriptionId) {
      setListedState(c.listing);
    } else if (c.t === "listing-cancelled" && c.inscriptionId === inscriptionId) {
      setListedState(null);
    } else if (c.t === "sale-pending" && c.inscriptionId === inscriptionId) {
      setListedState(null);
      setPendingSale();
    }
  }
}
